import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getAuth } from 'firebase/auth';
import { db } from '../../firebase-config';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  writeBatch,
  query,
  where
} from 'firebase/firestore';
import config from '../../config';
import tempAccessService from '../../services/tempAccessService';
import './AdminCommandPanel.css';

const AdminCommandPanelInner = ({ onClose }) => {
  const auth = getAuth();
  const { user } = useSelector(state => state.auth);

  const [activeTab, setActiveTab] = useState('permissions');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Data states
  const [users, setUsers] = useState([]);
  const [processedUsers, setProcessedUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  // Permission management state
  const [userPermissions, setUserPermissions] = useState({
    viewOffices: false,
    viewVehicles: [],
    viewTowers: false,
    viewGeofences: []
  });

  // Role switching state
  const [selectedNewRole, setSelectedNewRole] = useState('');

  // User removal state
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);

  // Role management state
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [showEditRole, setShowEditRole] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    permissions: {
      viewOffices: false,
      viewVehicles: [],
      viewTowers: false,
      viewGeofences: []
    }
  });
  const [editRole, setEditRole] = useState({
    name: '',
    description: '',
    permissions: {
      viewOffices: false,
      viewVehicles: [],
      viewTowers: false,
      viewGeofences: []
    }
  });

  // Dynamic options loaded from Firestore
  const [availableVehicles, setAvailableVehicles] = useState(['mobile', 'aurus', 'triton', 'vehicle', 'ebike', 'jeep']);
  const [availableGeofences, setAvailableGeofences] = useState(['beach']); // Only beach exists in current Firestore

  // Temporary Access state
  const [tempAccessEmail, setTempAccessEmail] = useState('');
  const [tempAccessDuration, setTempAccessDuration] = useState(1); // hours
  const [tempAccessRole, setTempAccessRole] = useState(''); // role - will be set when roles load
  const [showDurationDropdown, setShowDurationDropdown] = useState(false);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [tempAccessLinks, setTempAccessLinks] = useState([]);
  const [generatedLink, setGeneratedLink] = useState('');
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [tempAccessAutoTimeout, setTempAccessAutoTimeout] = useState(false);

  // Refs for auto-scrolling to forms
  const editFormRef = useRef(null);
  const createFormRef = useRef(null);

  // Clear messages
  const clearMessages = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  // Load available vehicle types from Firestore
  const loadAvailableVehicleTypes = useCallback(async () => {
    try {
      console.log('📡 Loading available vehicle types from Firestore...');
      
      // Try to get device types from the devices endpoint
      const response = await apiCall('/api/devices/locations');
      if (response.devices && response.devices.length > 0) {
        const deviceTypes = [...new Set(response.devices.map(device => device.device_type).filter(Boolean))];
        if (deviceTypes.length > 0) {
          console.log('✅ Loaded vehicle types from devices:', deviceTypes);
          setAvailableVehicles(deviceTypes);
          return;
        }
      }
    } catch (error) {
      console.warn('⚠️ Could not load vehicle types from devices, using system defaults');
    }
    
    // Fallback to system defaults based on backend analysis
    const defaultVehicleTypes = ['mobile', 'aurus', 'triton', 'vehicle', 'ebike', 'jeep'];
    console.log('📋 Using default vehicle types:', defaultVehicleTypes);
    setAvailableVehicles(defaultVehicleTypes);
  }, []);

  // Load available geofence types from Firestore
  const loadAvailableGeofenceTypes = useCallback(async () => {
    try {
      console.log('📡 Loading available geofence types from Firestore...');
      const geofenceTypes = new Set();
      
      // Check towers
      try {
        const towersResponse = await apiCall('/api/towers');
        if (towersResponse.towers && towersResponse.towers.length > 0) {
          geofenceTypes.add('tower');
        }
      } catch (error) {
        // Silently fail - CORS errors are expected when backend isn't configured
      }
      
      // Check beaches  
      try {
        const beachesResponse = await apiCall('/api/beaches');
        if (beachesResponse.beaches && beachesResponse.beaches.length > 0) {
          geofenceTypes.add('beach');
        }
      } catch (error) {
        // Silently fail - CORS errors are expected when backend isn't configured
      }
      
      // Check offices
      try {
        const officesResponse = await apiCall('/api/offices');
        if (officesResponse.offices && officesResponse.offices.length > 0) {
          geofenceTypes.add('office');
        }
      } catch (error) {
        // Silently fail - CORS errors are expected when backend isn't configured
      }
      
      const types = Array.from(geofenceTypes);
      if (types.length > 0) {
        console.log('✅ Loaded geofence types from Firestore:', types);
        setAvailableGeofences(types);
      } else {
        // Based on current Firestore data, only beach exists
        console.log('📋 Using default geofence types (only beach exists in Firestore)');
        setAvailableGeofences(['beach']);
      }
    } catch (error) {
      console.warn('⚠️ Could not load geofence types, using defaults');
      setAvailableGeofences(['beach']); // Only beach exists in current system
    }
  }, []);

  // API call helper
  const apiCall = async (endpoint, options = {}) => {
    try {
      let token = null;
      try {
        token = await auth.currentUser?.getIdToken();
      } catch (authError) {
        console.warn('⚠️ No auth token available, using development mode token');
        token = 'dev-mode-token'; // Fallback for development
      }
      
      // Only log API calls in development if not expecting CORS errors
      // (CORS errors are expected when backend doesn't allow cross-origin requests)
      
      const response = await fetch(`${config.apiUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers
        }
      }).catch(fetchError => {
        // Silently catch CORS/network errors - Firestore fallback will handle it
        throw new Error('CORS_ERROR'); // Special error code for CORS/network errors
      });

      // Only log successful responses
      if (response.ok) {
      console.log('📡 API Response status:', response.status);
      }
      
      if (!response.ok) {
        let errorText = '';
        try {
          errorText = await response.text();
        } catch (e) {
          errorText = 'Unable to read error response';
        }
        // Only log non-404/500 errors (CORS won't reach here, but other errors might)
        if (response.status !== 404 && response.status !== 500) {
        console.error('❌ API Error Response:', errorText);
        }
        throw new Error(`API call failed: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`);
      }

      const result = await response.json();
      // Only log successful API responses
      console.log('✅ API Success Response:', result);
      return result;
    } catch (error) {
      // Completely silence CORS errors - they're expected when backend doesn't allow cross-origin
      // The Firestore fallback will handle data loading
      if (error.message === 'CORS_ERROR' || error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
        // Silently fail - Firestore fallback will be used
        throw new Error('CORS_ERROR');
      }
      // Only log unexpected errors
      console.error('❌ API Call Error:', error);
      throw error;
    }
  };

  // Fetch users from API with Firestore fallback
  const fetchUsers = useCallback(async () => {
    try {
      console.log('📡 Fetching users from API...');
      const response = await apiCall('/api/admin/users');
      const users = response.users || [];
      console.log('✅ Users fetched from API:', users.length, 'users');
      setUsers(users);
      return users;
    } catch (error) {
      // Only show warning if it's not a CORS error (CORS is expected when backend isn't configured)
      if (!error.message.includes('CORS') && !error.message.includes('Unable to connect')) {
        console.warn('⚠️ API failed, falling back to Firestore for users:', error.message);
      }
      try {
        // Fallback to Firestore
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(usersRef);
        const usersData = usersSnapshot.docs.map(doc => ({
          uid: doc.id,
          ...doc.data()
        }));
        console.log('✅ Users fetched from Firestore:', usersData.length, 'users');
        setUsers(usersData);
        return usersData;
      } catch (firestoreError) {
        console.error('❌ Firestore fallback also failed:', firestoreError);
        setError(`Failed to fetch users from both API and Firestore. The admin panel may have limited functionality.`);
        return [];
      }
    }
  }, []);

  // Fetch organizations from API with Firestore fallback
  const fetchOrganizations = useCallback(async () => {
    try {
      console.log('📡 Fetching organizations from API...');
      const response = await apiCall('/api/admin/organizations');
      const organizations = response.organizations || [];
      console.log('✅ Organizations fetched from API:', organizations.length, 'organizations');
      setOrganizations(organizations);
      return organizations;
    } catch (error) {
      // Only show warning if it's not a CORS error (CORS is expected when backend isn't configured)
      if (!error.message.includes('CORS') && !error.message.includes('Unable to connect')) {
        console.warn('⚠️ API failed, falling back to Firestore for organizations:', error.message);
      }
      try {
        // Fallback to Firestore
        const orgsRef = collection(db, 'organizations');
        const orgsSnapshot = await getDocs(orgsRef);
        const orgsData = orgsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('✅ Organizations fetched from Firestore:', orgsData.length, 'organizations');
        setOrganizations(orgsData);
        return orgsData;
      } catch (firestoreError) {
        console.error('❌ Firestore fallback also failed:', firestoreError);
        setError(`Failed to fetch organizations from both API and Firestore. The admin panel may have limited functionality.`);
        return [];
      }
    }
  }, []);

  // Fetch roles from API with Firestore fallback
  const fetchRoles = useCallback(async () => {
    try {
      console.log('📡 Fetching roles from API...');
      const response = await apiCall('/api/admin/roles');
      const roles = response.roles || [];
      console.log('✅ Roles fetched from API:', roles.length, 'roles');
      setRoles(roles);
      return roles;
    } catch (error) {
      // Only show warning if it's not a CORS error (CORS is expected when backend isn't configured)
      if (!error.message.includes('CORS') && !error.message.includes('Unable to connect')) {
        console.warn('⚠️ API failed, falling back to Firestore for roles:', error.message);
      }
      try {
        // Fallback to Firestore
        const rolesRef = collection(db, 'roles');
        const rolesSnapshot = await getDocs(rolesRef);
        const rolesData = rolesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('✅ Roles fetched from Firestore:', rolesData.length, 'roles');
        setRoles(rolesData);
        return rolesData;
      } catch (firestoreError) {
        console.error('❌ Firestore fallback also failed:', firestoreError);
        setError(`Failed to fetch roles from both API and Firestore. The admin panel may have limited functionality.`);
        return [];
      }
    }
  }, []);

  // Fetch data from Firestore
  const fetchFirestoreData = useCallback(async () => {
    console.log('🔥 Fetching data from Firestore...');

    const usersRef = collection(db, 'users');
    const orgsRef = collection(db, 'organizations');
    const rolesRef = collection(db, 'roles');

    const [usersSnapshot, orgsSnapshot, rolesSnapshot] = await Promise.all([
      getDocs(usersRef),
      getDocs(orgsRef),
      getDocs(rolesRef)
    ]);

    const usersData = usersSnapshot.docs.map(doc => ({
      uid: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt || new Date().toISOString(),
      ...doc.data()
    }));

    const orgsData = orgsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt || new Date().toISOString(),
      ...doc.data()
    }));

    const rolesData = rolesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt || new Date().toISOString(),
      ...doc.data()
    }));

    console.log('🔄 Setting state with processed data...');
    setUsers(usersData);
    setOrganizations(orgsData);
    setRoles(rolesData);

    console.log('✅ State set - Users:', usersData.length, 'Orgs:', orgsData.length, 'Roles:', rolesData.length);

    console.log('👥 Users fetched from Firestore:', usersData.length, 'users');
    console.log('📋 Sample user data:', usersData[0]);
    console.log('🏢 Organizations fetched from Firestore:', orgsData.length, 'organizations');
    console.log('📋 Sample organization data:', orgsData[0]);
    console.log('🔐 Roles fetched from Firestore:', rolesData.length, 'roles');
    console.log('📋 Sample role data:', rolesData[0]);

    setSuccess(`Real data loaded successfully from Firestore! Found ${usersData.length} users, ${orgsData.length} organizations, ${rolesData.length} roles.`);

  }, []);

  // Setup Firestore listeners
  const setupFirestoreListeners = useCallback(() => {
    console.log('👂 Setting up Firestore listeners...');

    const unsubscribeUsers = onSnapshot(collection(db, 'users'), (snapshot) => {
      console.log('🔄 Users collection updated');
    });

    const unsubscribeOrgs = onSnapshot(collection(db, 'organizations'), (snapshot) => {
      console.log('🔄 Organizations collection updated');
    });

    const unsubscribeRoles = onSnapshot(collection(db, 'roles'), (snapshot) => {
      console.log('🔄 Roles collection updated');
    });

    return () => {
      console.log('🧹 Cleaning up Firestore listeners...');
      unsubscribeUsers();
      unsubscribeOrgs();
      unsubscribeRoles();
    };
  }, []);

  // Load data on component mount and setup Firestore listeners
  useEffect(() => {
    let cleanup = null;

    const loadData = async () => {
      try {
        // Use API first as it provides properly structured data with roles
        console.log('🚀 Loading data using API endpoints...');
        
        // Fetch all data - each function handles its own errors gracefully
        // Use Promise.allSettled to ensure all calls complete even if some fail
        const results = await Promise.allSettled([
          fetchUsers(),
          fetchOrganizations(),
          fetchRoles(),
          loadAvailableVehicleTypes(),
          loadAvailableGeofenceTypes()
        ]);
        
        // Log any failures
        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            const names = ['users', 'organizations', 'roles', 'vehicle types', 'geofence types'];
            console.warn(`⚠️ Failed to load ${names[index]}:`, result.reason);
          }
        });
        
        console.log('✅ Data loading completed (some may have failed)');
        cleanup = setupFirestoreListeners();
      } catch (error) {
        console.error('AdminCommandPanel: Unexpected error during data loading:', error);
        setError(`An unexpected error occurred: ${error.message}`);
        // Still try to setup Firestore listeners
        try {
          cleanup = setupFirestoreListeners();
        } catch (firestoreError) {
          console.error('Firestore listener setup failed:', firestoreError);
        }
      }
    };

    loadData();

    // Cleanup listeners on unmount
    return () => {
      if (cleanup && typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, [fetchUsers, fetchOrganizations, fetchRoles, setupFirestoreListeners, loadAvailableVehicleTypes, loadAvailableGeofenceTypes]);

  // Process users data
  useEffect(() => {
    const processed = users.map(user => {
      // If user already has roles from API (which is the preferred way), use them
      if (user.roles && Array.isArray(user.roles) && user.roles.length > 0) {
        console.log('✅ Using API roles for user:', user.email, user.roles);
        return user;
      }
      
      // Fallback: build roles from organizations data (for direct Firestore access)
      const userOrgs = organizations.filter(org =>
        org.members?.some(member => member.uid === user.uid)
      );

      const processedUser = {
        ...user,
        roles: userOrgs.map(org => ({
          organizationId: org.id,
          role: org.members?.find(member => member.uid === user.uid)?.role || 'user'
        }))
      };
      
      console.log('🔄 Built roles from organizations for user:', user.email, processedUser.roles);
      return processedUser;
    });

    console.log('📊 Processed users:', processed.length, 'users with roles');
    setProcessedUsers(processed);
  }, [users, organizations]);

  // Load temporary access links function
  const loadTempAccessLinks = useCallback(async () => {
    if (!user?.uid) {
      console.log('🚫 No user UID available, skipping loadTempAccessLinks');
      return;
    }

    try {
      console.log('🔄 Loading temporary access links for user:', user.uid);
      
      // Use frontend temporary access service
      const links = await tempAccessService.getTempAccessLinks(user.uid);
      setTempAccessLinks(links);
      console.log('✅ Loaded temporary access links:', links.length);

    } catch (error) {
      console.error('❌ Error loading temporary access links:', error);
    }
  }, [user?.uid]);

  // Load temporary access links when tempAccess tab is active
  useEffect(() => {
    if (activeTab === 'tempAccess' && user?.uid) {
      loadTempAccessLinks();
    }
  }, [activeTab, loadTempAccessLinks, user?.uid]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDurationDropdown && !event.target.closest('[data-duration-dropdown]')) {
        setShowDurationDropdown(false);
      }
      if (showRoleDropdown && !event.target.closest('[data-role-dropdown]')) {
        setShowRoleDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDurationDropdown, showRoleDropdown]);

  // Set default role when roles are loaded
  useEffect(() => {
    if (roles.length > 0 && !tempAccessRole) {
      setTempAccessRole(roles[0].id); // Use role ID instead of name
    }
  }, [roles, tempAccessRole]);

  // Real-time listener for temporary access changes
  useEffect(() => {
    if (activeTab === 'tempAccess' && user?.uid) {
      const tempAccessRef = collection(db, 'temporary_access');
      const q = query(tempAccessRef, where('createdBy', '==', user.uid), where('isActive', '==', true));
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const links = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          links.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() || data.createdAt,
            expiresAt: data.expiresAt?.toDate?.() || data.expiresAt
          });
        });
        setTempAccessLinks(links);
        console.log('🔄 Real-time update: Temporary access links refreshed');
      }, (error) => {
        console.error('❌ Error listening to temporary access changes:', error);
      });

      return () => unsubscribe();
    }
  }, [activeTab, user?.uid]);

  // Get role display name
  const getRoleDisplayName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : roleId;
  };

  // Get organization name
  const getOrganizationName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org ? org.name : orgId;
  };

  // Handle user select
  const handleUserSelect = useCallback((user) => {
    if (!user) {
      setSelectedUser(null);
      setUserPermissions({
        viewOffices: false,
        viewVehicles: [],
        viewTowers: false,
        viewGeofences: []
      });
      return;
    }

    setSelectedUser(user);

    // Load user's individual permissions if they exist, otherwise load role defaults
    if (user.permissions) {
      console.log('👤 Loading individual user permissions');
      setUserPermissions(user.permissions);
    } else {
      console.log('🏷️ Loading role default permissions for user');
      // Fallback to role permissions
    if (user.roles && user.roles.length > 0) {
      const userRole = user.roles[0];
      const role = roles.find(r => r.id === userRole.role);
      if (role && role.permissions) {
        setUserPermissions({
          viewOffices: role.permissions.viewOffices || false,
          viewVehicles: role.permissions.viewVehicles === true ? true : (Array.isArray(role.permissions.viewVehicles) ? role.permissions.viewVehicles : []),
          viewTowers: role.permissions.viewTowers || false,
          viewGeofences: role.permissions.viewGeofences === true ? true : (Array.isArray(role.permissions.viewGeofences) ? role.permissions.viewGeofences : [])
        });
      } else {
        setUserPermissions({
          viewOffices: false,
          viewVehicles: [],
          viewTowers: false,
          viewGeofences: []
        });
      }
    } else {
      setUserPermissions({
        viewOffices: false,
        viewVehicles: [],
        viewTowers: false,
        viewGeofences: []
      });
      }
    }
  }, [roles]);


  // Update individual user permissions
  const updateIndividualUserPermissions = async (userId, permissions) => {
    try {
      console.log('🔄 Updating individual user permissions in Firestore:', userId, permissions);

      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        permissions: permissions,
        updatedAt: new Date().toISOString()
      });

      console.log('✅ User permissions updated in Firestore');

      // Update local state
      setUsers(prev => prev.map(u =>
        u.uid === userId
          ? { ...u, permissions: permissions, updatedAt: new Date().toISOString() }
          : u
      ));

    } catch (error) {
      console.error('❌ Error updating user permissions:', error);
      throw error;
    }
  };

  // Handle role switch
  const handleRoleSwitch = (roleId) => {
    setSelectedNewRole(roleId);
  };

  // Switch user role
  const handleSwitchRole = async () => {
    if (!selectedUser || !selectedNewRole) {
      setError('Please select a user and a new role');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      console.log('🔄 Switching user role:', { user: selectedUser.uid, newRole: selectedNewRole });

      // Update user's role using the comprehensive Firestore update
      if (selectedUser.roles && selectedUser.roles.length > 0) {
        await updateUserRoleInFirestore(selectedUser.uid, selectedUser.roles[0].organizationId, selectedNewRole, selectedUser.email);

        setSuccess(`✅ User role switched to ${getRoleDisplayName(selectedNewRole)} successfully! Changes are live in Firestore.`);
        setSelectedNewRole(''); // Reset selection
        console.log('✅ Role switch completed');

        // Update the selected user's permissions based on new role
        const newRole = roles.find(r => r.id === selectedNewRole);
        if (newRole && newRole.permissions) {
          setUserPermissions({
            viewOffices: newRole.permissions.viewOffices || false,
            viewVehicles: Array.isArray(newRole.permissions.viewVehicles) ? newRole.permissions.viewVehicles : [],
            viewTowers: newRole.permissions.viewTowers || false,
            viewGeofences: Array.isArray(newRole.permissions.viewGeofences) ? newRole.permissions.viewGeofences : []
          });
          console.log('🎛️ Updated permission UI based on new role');
        }
      } else {
        setError('User has no roles to switch');
      }

    } catch (err) {
      console.error('Role switch error:', err);
      setError(`Failed to switch role: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to convert email to document ID format
  const emailToDocId = (email) => {
    if (!email) return null;
    return email.replace(/[.@]/g, (match) => {
      if (match === '.') return '_';
      if (match === '@') return '_at_';
      return match;
    });
  };

  // Update user role in Firestore using subcollection structure
  // Uses email-normalized format for document ID (consistent with backend)
  const updateUserRoleInFirestore = async (userId, organizationId, newRoleId, userEmail = null) => {
    try {
      console.log('🔄 Updating user role in Firestore subcollection:', { userId, organizationId, newRoleId, userEmail });

      // Get user email if not provided
      let email = userEmail;
      if (!email) {
        try {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            email = userDoc.data().email;
          } else {
            // Try to get email from users collection by email (if userId is actually an email)
            if (userId.includes('@')) {
              email = userId;
            }
          }
        } catch (e) {
          console.warn('⚠️ Could not fetch user email:', e);
        }
      }

      // Use email-normalized format for document ID (preferred)
      // Fallback to userId if email not available (backward compatibility)
      const memberDocId = email ? emailToDocId(email) : userId;
      const memberRef = doc(db, 'organizations', organizationId, 'members', memberDocId);
      
      try {
        let memberSnap = await getDoc(memberRef);
        
        // If not found with email-based ID and we have userId, try UID-based lookup (backward compatibility)
        if (!memberSnap.exists() && userId && userId !== memberDocId) {
          console.log('⚠️ Member not found with email-based ID, trying UID-based lookup for backward compatibility');
          const uidBasedRef = doc(db, 'organizations', organizationId, 'members', userId);
          memberSnap = await getDoc(uidBasedRef);
          
          if (memberSnap.exists()) {
            // Found with UID, but we want to migrate to email-based ID
            console.log('🔄 Migrating member from UID-based to email-based document ID');
            const oldData = memberSnap.data();
            
            // Create new document with email-based ID
            await setDoc(memberRef, {
              ...oldData,
              role: newRoleId,
              updatedAt: new Date().toISOString(),
              migratedFrom: userId,
              migratedAt: new Date().toISOString()
            });
            
            // Delete old UID-based document
            await deleteDoc(uidBasedRef);
            console.log('✅ Member migrated to email-based document ID');
            return;
          }
        }
        
        if (memberSnap.exists()) {
          await updateDoc(memberRef, {
            role: newRoleId,
            updatedAt: new Date().toISOString()
          });
          console.log('✅ User role updated in subcollection');
        } else {
          console.warn('⚠️ Member document not found in subcollection, creating new one');
          await setDoc(memberRef, {
            role: newRoleId,
            email: email || '',
            displayName: email ? email.split('@')[0] : '',
            joinedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            uid: memberDocId,
            originalUid: userId !== memberDocId ? userId : undefined
          });
          console.log('✅ New member document created with role (email-based ID)');
        }
      } catch (subcollectionError) {
        console.warn('⚠️ Subcollection approach failed, trying alternative approach:', subcollectionError);
        
        // Alternative: Try to update/create in the main organization document
        const orgRef = doc(db, 'organizations', organizationId);
        const orgSnap = await getDoc(orgRef);
        
        if (orgSnap.exists()) {
          const orgData = orgSnap.data();
          
          // Check if members array exists, if not create it
          let members = orgData.members || [];
          
          // Find and update existing member or add new one
          const memberIndex = members.findIndex(member => member.uid === userId);
          if (memberIndex >= 0) {
            members[memberIndex] = { ...members[memberIndex], role: newRoleId, updatedAt: new Date().toISOString() };
          } else {
            members.push({ uid: userId, role: newRoleId, joinedAt: new Date().toISOString() });
          }
          
          await updateDoc(orgRef, {
            members: members,
            updatedAt: new Date().toISOString()
          });
          console.log('✅ User role updated in organization document members array');
        }
      }

      // Refresh the data to get the updated state
      console.log('🔄 Refreshing user and organization data...');
      await Promise.all([
        fetchUsers(),
        fetchOrganizations()
      ]);
      console.log('✅ Data refreshed successfully');

    } catch (error) {
      console.error('❌ Error updating user role:', error);
      throw error;
    }
  };

  // Initiate user removal
  const initiateUserRemoval = (user) => {
    setUserToRemove(user);
    setShowRemoveConfirm(true);
  };

  // Cancel user removal
  const cancelUserRemoval = () => {
    setUserToRemove(null);
    setShowRemoveConfirm(false);
  };

  // Delete user from Firestore
  const deleteUserFromFirestore = async () => {
    if (!userToRemove) return;

    try {
      setIsLoading(true);
      console.log('🗑️ Deleting user from Firestore:', userToRemove.uid);

      // Delete user document
      const userRef = doc(db, 'users', userToRemove.uid);
      await deleteDoc(userRef);

      // Remove user from all organizations
      const batch = writeBatch(db);

      organizations.forEach(org => {
        if (org.members?.some(member => member.uid === userToRemove.uid)) {
          const orgRef = doc(db, 'organizations', org.id);
          const updatedMembers = org.members.filter(member => member.uid !== userToRemove.uid);
          batch.update(orgRef, { members: updatedMembers });
        }
      });

      await batch.commit();

      console.log('✅ User deleted from Firestore');

      // Update local state
      setUsers(prev => prev.filter(u => u.uid !== userToRemove.uid));
      setOrganizations(prev => prev.map(org => ({
        ...org,
        members: org.members?.filter(member => member.uid !== userToRemove.uid) || []
      })));

      setSuccess(`✅ User ${userToRemove.displayName || userToRemove.email} has been permanently removed from the system.`);
      setUserToRemove(null);
      setShowRemoveConfirm(false);

    } catch (error) {
      console.error('❌ Error deleting user:', error);
      setError(`Failed to delete user: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle user removal
  const handleRemoveUser = async (userId, organizationId) => {
    try {
      setIsLoading(true);
      console.log('👤 Removing user from organization:', { userId, organizationId });

      // Update organization to remove user
      const orgRef = doc(db, 'organizations', organizationId);
      const orgSnap = await getDoc(orgRef);

      if (orgSnap.exists()) {
        const orgData = orgSnap.data();
        const updatedMembers = orgData.members.filter(member => member.uid !== userId);

        await updateDoc(orgRef, {
          members: updatedMembers,
          updatedAt: new Date().toISOString()
        });

        console.log('✅ User removed from organization');

        // Update local state
        setOrganizations(prev => prev.map(org =>
          org.id === organizationId
            ? { ...org, members: updatedMembers, updatedAt: new Date().toISOString() }
            : org
        ));

        setSuccess(`✅ User removed from ${getOrganizationName(organizationId)} successfully!`);
      }

    } catch (error) {
      console.error('❌ Error removing user from organization:', error);
      setError(`Failed to remove user: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Create custom role
  const createCustomRole = async () => {
    if (!newRole.name.trim()) {
      setError('Role name is required');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      console.log('🔄 Creating custom role:', newRole);

      // Create role in Firestore
      const roleRef = doc(collection(db, 'roles'));
      await setDoc(roleRef, {
        ...newRole,
        id: roleRef.id,
        isCustom: true,
        createdAt: new Date().toISOString(),
        createdBy: user?.uid || 'admin'
      });

      console.log('✅ Custom role created');

      // Update local state
      const newRoleData = {
        ...newRole,
        id: roleRef.id,
        isCustom: true,
        createdAt: new Date().toISOString(),
        createdBy: user?.uid || 'admin'
      };

      setRoles(prev => [...prev, newRoleData]);
      setSuccess(`✅ Custom role "${newRole.name}" created successfully!`);

      // Reset form
      setNewRole({
        name: '',
        description: '',
        permissions: {
          viewOffices: false,
          viewVehicles: [],
          viewTowers: false,
          viewGeofences: []
        }
      });

      setShowCreateRole(false);

    } catch (error) {
      console.error('❌ Error creating custom role:', error);
      setError(`Failed to create role: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Start editing role
  const startEditRole = (role) => {
    setEditingRole(role);
    setEditRole({
      name: role.name,
      description: role.description || '',
      permissions: {
        viewOffices: role.permissions?.viewOffices || false,
        viewVehicles: Array.isArray(role.permissions?.viewVehicles) ? role.permissions.viewVehicles : [],
        viewTowers: role.permissions?.viewTowers || false,
        viewGeofences: Array.isArray(role.permissions?.viewGeofences) ? role.permissions.viewGeofences : []
      }
    });
    setShowEditRole(true);
    
    // Auto-scroll to edit form after a brief delay to ensure it's rendered
    setTimeout(() => {
      if (editFormRef.current) {
        editFormRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
        console.log('📍 Scrolled to edit form for role:', role.name);
      }
    }, 100);
  };

  // Cancel edit role
  const cancelEditRole = () => {
    setEditingRole(null);
    setEditRole({
      name: '',
      description: '',
      permissions: {
        viewOffices: false,
        viewVehicles: [],
        viewTowers: false,
        viewGeofences: []
      }
    });
    setShowEditRole(false);
  };

  // Update role permissions
  const updateRolePermissions = async () => {
    if (!editingRole) return;

    try {
      setIsLoading(true);
      setError(null);

      console.log('🔄 Updating role permissions:', editingRole.id, editRole);

      // Update role in Firestore
      const roleRef = doc(db, 'roles', editingRole.id);
      await updateDoc(roleRef, {
        ...editRole,
        updatedAt: new Date().toISOString()
      });

      console.log('✅ Role permissions updated');

      // Update local state
      setRoles(prev => prev.map(role =>
        role.id === editingRole.id
          ? { ...role, ...editRole, updatedAt: new Date().toISOString() }
          : role
      ));

      setSuccess(`✅ Role "${editRole.name}" permissions updated successfully!`);

      setEditingRole(null);
      setShowEditRole(false);

    } catch (error) {
      console.error('❌ Error updating role permissions:', error);
      setError(`Failed to update role: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete custom role
  const deleteCustomRole = async (roleId) => {
    try {
      setIsLoading(true);
      console.log('🗑️ Deleting custom role:', roleId);

      // Delete role from Firestore
      const roleRef = doc(db, 'roles', roleId);
      await deleteDoc(roleRef);

      console.log('✅ Custom role deleted');

      // Update local state
      setRoles(prev => prev.filter(role => role.id !== roleId));

      setSuccess('✅ Custom role deleted successfully!');

    } catch (error) {
      console.error('❌ Error deleting custom role:', error);
      setError(`Failed to delete role: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Temporary Access Functions
  const generateTempAccessLink = async () => {
    console.log('🔄 Generating temporary access link...');
    console.log('📧 Email:', tempAccessEmail);
    console.log('⏰ Duration:', tempAccessDuration, 'hours');
    console.log('👤 User UID:', user?.uid);
    
    if (!tempAccessEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    if (!tempAccessEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setIsGeneratingLink(true);
      setError(null);

      console.log('🔄 Using frontend temporary access service...');
      
      // Use frontend temporary access service
      const result = await tempAccessService.createTempAccess(
        tempAccessEmail.trim(),
        tempAccessDuration,
        tempAccessRole,
        user.uid,
        tempAccessAutoTimeout
      );

      if (result.success) {
        setGeneratedLink(result.accessLink);
        
        // Create success message
        let successMessage = `✅ Temporary access link generated and sent to ${tempAccessEmail}`;
        successMessage += `\n🔗 Link: ${result.accessLink}`;
        successMessage += `\n⏰ Expires: ${result.expiresAt.toLocaleString()}`;
        successMessage += `\n👤 Role: ${result.role}`;
        
        setSuccess(successMessage);
        
        // Clear form
        setTempAccessEmail('');
        setTempAccessDuration(1);
        setTempAccessRole(roles.length > 0 ? roles[0].id : '');
        setShowDurationDropdown(false);
        setShowRoleDropdown(false);
        
        // Manually add the new link to the state is not needed as we have a real-time listener
        // The listener will pick up the new link and update the UI automatically
        // This prevents race conditions that cause duplicate entries
        
      } else {
        setError(result.error || 'Failed to generate temporary access link');
      }

    } catch (error) {
      console.error('❌ Error generating temporary access link:', error);
      setError(`Failed to generate link: ${error.message}`);
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const copyTempAccessLink = async () => {
    if (!generatedLink) {
      setError('No link to copy');
      return;
    }

    try {
      await navigator.clipboard.writeText(generatedLink);
      setSuccess('✅ Link copied to clipboard!');
    } catch (error) {
      console.error('❌ Error copying link:', error);
      setError('Failed to copy link to clipboard');
    }
  };


  const revokeTempAccess = async (linkId) => {
    try {
      setIsLoading(true);

      console.log('🔄 Revoking temporary access link:', linkId);
      
      // Use frontend temporary access service
      const result = await tempAccessService.revokeTempAccess(linkId);

      if (result.success) {
        setSuccess('✅ Temporary access link revoked successfully');
        // No need to manually refresh - real-time listener will handle it
      } else {
        setError(result.error || 'Failed to revoke temporary access link');
      }

    } catch (error) {
      console.error('❌ Error revoking temporary access link:', error);
      setError(`Failed to revoke link: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const cleanupInvalidEntries = async () => {
    try {
      setIsLoading(true);
      
      console.log('🧹 Cleaning up expired temporary access entries...');
      
      // Use frontend temporary access service
      await tempAccessService.cleanupExpiredTokens();
      
      setSuccess('✅ Cleaned up expired temporary access entries');
      // No need to manually refresh - real-time listener will handle it

    } catch (error) {
      console.error('❌ Error cleaning up invalid entries:', error);
      setError(`Failed to cleanup: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div>
      <div className="admin-command-panel-overlay">
        <div className="admin-command-panel">
          <div className="admin-command-panel-header">
            <h2>Admin Command Control Panel</h2>
            <div className="header-actions">
              <button onClick={onClose} className="close-button">&times;</button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="admin-tabs">
            <button
              className={`tab-button ${activeTab === 'permissions' ? 'active' : ''}`}
              onClick={() => setActiveTab('permissions')}
            >
              Permissions
            </button>
            <button
              className={`tab-button ${activeTab === 'roles' ? 'active' : ''}`}
              onClick={() => setActiveTab('roles')}
            >
              Roles ({roles.length})
            </button>
            <button
              className={`tab-button ${activeTab === 'tempAccess' ? 'active' : ''}`}
              onClick={() => setActiveTab('tempAccess')}
            >
              Temporary Access
            </button>
          </div>

          {/* Tab Content */}
          <div className="admin-tab-content">
            <>
            {isLoading && <div className="loading-spinner">Loading...</div>}



            {/* Permissions Tab */}
            {activeTab === 'permissions' && (
              <div className="permissions-tab">
                <h3>User Role Management</h3>

                {/* User Selection */}
                <div className="permission-section">
                  <h4>Select User to Change Role</h4>
                  <select
                    value={selectedUser ? selectedUser.uid : ''}
                    onChange={(e) => {
                      const user = processedUsers.find(u => u.uid === e.target.value);
                      handleUserSelect(user);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '3px solid #3B82F6',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      backgroundColor: '#FFFFFF',
                      cursor: 'pointer',
                      outline: 'none',
                      display: 'block',
                      visibility: 'visible',
                      opacity: '1',
                      marginBottom: '16px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      fontWeight: '500'
                    }}
                  >
                    <option value="">-- Select a User --</option>
                    {processedUsers.map(user => (
                      <option key={user.uid} value={user.uid}>
                        {user.displayName || user.email} ({user.roles && user.roles.length > 0 ? getRoleDisplayName(user.roles[0].role) : 'No Role'})
                      </option>
                    ))}
                  </select>

                </div>

                {selectedUser && (
                  <div className="permission-details">
                    {/* Role Switching */}
                    <div className="role-switching-section" style={{
                      marginBottom: '20px',
                      padding: '16px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      backgroundColor: '#F9FAFB'
                    }}>
                      <h4 style={{ margin: '0 0 12px 0', color: '#374151' }}>Switch User Role</h4>
                      <div className="role-switch-controls" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <label htmlFor="role-select" style={{ fontSize: '0.9rem', fontWeight: '500', color: '#374151' }}>
                          Change to:
                        </label>
                      <select
                        id="role-select"
                        value={selectedNewRole}
                        onChange={(e) => {
                          console.log('Role selection changed:', e.target.value);
                          handleRoleSwitch(e.target.value);
                        }}
                        style={{
                          padding: '12px 16px',
                          border: '3px solid #3B82F6',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          backgroundColor: '#FFFFFF',
                          cursor: 'pointer',
                          outline: 'none',
                          minWidth: '250px',
                          fontWeight: '500',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          display: 'block',
                          visibility: 'visible',
                          opacity: '1'
                        }}
                      >
                        <option value="">Select new role...</option>
                          {roles.map(role => (
                            <option key={role.id} value={role.id}>
                              {role.name}
                            </option>
                          ))}
                      </select>
                      <button
                        onClick={handleSwitchRole}
                        disabled={!selectedNewRole}
                          style={{
                            padding: '12px 20px',
                            backgroundColor: selectedNewRole ? '#3B82F6' : '#9CA3AF',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            cursor: selectedNewRole ? 'pointer' : 'not-allowed',
                            fontWeight: '600',
                            boxShadow: selectedNewRole ? '0 2px 4px rgba(59, 130, 246, 0.3)' : 'none',
                            transition: 'all 0.2s'
                          }}
                        >
                          {selectedNewRole ? `Switch to ${roles.find(r => r.id === selectedNewRole)?.name || 'Selected Role'}` : 'Select Role First'}
                      </button>
                    </div>

                  </div>
                  </div>
                )}

                {/* User Removal Section */}
                {selectedUser && (
                  <div className="permission-section" style={{
                    marginTop: '20px',
                    padding: '16px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    backgroundColor: '#FEF2F2'
                  }}>
                    <h4 style={{ margin: '0 0 12px 0', color: '#DC2626', fontSize: '1rem' }}>
                      Danger Zone
                    </h4>
                    <p style={{ margin: '0 0 16px 0', color: '#6B7280', fontSize: '0.9rem' }}>
                      Remove this user from the system. This action will delete the user from Firestore and cannot be undone.
                    </p>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '4px' }}>
                          <strong>Selected User:</strong> {selectedUser.displayName || selectedUser.email}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#6B7280' }}>
                          UID: {selectedUser.uid}
                        </div>
                      </div>

                      <button
                        onClick={() => initiateUserRemoval(selectedUser)}
                        style={{
                          background: '#EF4444',
                          color: 'white',
                          border: 'none',
                          padding: '10px 20px',
                          borderRadius: '6px',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.background = '#DC2626'}
                        onMouseOut={(e) => e.target.style.background = '#EF4444'}
                      >
                        Remove User
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Roles Tab */}
            {activeTab === 'roles' && (
              <div className="roles-tab">
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '20px' 
                }}>
                  <h3 style={{ margin: '0' }}>Role Management</h3>
                  <button
                    onClick={() => {
                      const newShowState = !showCreateRole;
                      setShowCreateRole(newShowState);
                      
                      // If opening the create form, scroll to it
                      if (newShowState) {
                        setTimeout(() => {
                          if (createFormRef.current) {
                            createFormRef.current.scrollIntoView({ 
                              behavior: 'smooth', 
                              block: 'start',
                              inline: 'nearest'
                            });
                            console.log('📍 Scrolled to create role form');
                          }
                        }, 100);
                      }
                    }}
                    style={{
                      background: showCreateRole ? '#EF4444' : '#3B82F6',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {showCreateRole ? (
                      <>
                        <span>✕</span>
                        <span>Cancel</span>
                      </>
                    ) : (
                      <>
                        <span>+</span>
                        <span>Create New Role</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Create Role Form */}
                {showCreateRole && (
                  <div ref={createFormRef} style={{
                    marginBottom: '30px',
                    padding: '20px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    backgroundColor: '#F9FAFB'
                  }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#374151' }}>Create Custom Role</h4>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        Role Name *
                      </label>
                      <input
                        type="text"
                        value={newRole.name}
                        onChange={(e) => setNewRole(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Enter role name"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        Description
                      </label>
                      <textarea
                        value={newRole.description}
                        onChange={(e) => setNewRole(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Enter role description"
                        rows="3"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          fontSize: '1rem',
                          resize: 'vertical'
                        }}
                      />
                    </div>

                    {/* Permissions */}
                    <div style={{ marginBottom: '20px' }}>
                      <h5 style={{ margin: '0 0 12px 0', color: '#374151' }}>Permissions</h5>

                      {/* View Offices */}
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                            checked={newRole.permissions.viewOffices}
                            onChange={(e) => setNewRole(prev => ({
                              ...prev,
                              permissions: { ...prev.permissions, viewOffices: e.target.checked }
                            }))}
                          />
                          <span>View Offices</span>
                      </label>
                    </div>

                      {/* View Towers */}
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                            checked={newRole.permissions.viewTowers}
                            onChange={(e) => setNewRole(prev => ({
                              ...prev,
                              permissions: { ...prev.permissions, viewTowers: e.target.checked }
                            }))}
                          />
                          <span>View Towers</span>
                      </label>
                    </div>

                      {/* Vehicle Access */}
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <input
                            type="checkbox"
                            checked={newRole.permissions.viewVehicles === true}
                            onChange={(e) => setNewRole(prev => ({
                              ...prev,
                              permissions: {
                                ...prev.permissions,
                                viewVehicles: e.target.checked ? true : []
                              }
                            }))}
                          />
                          <span>View All Vehicles</span>
                        </label>

                        {newRole.permissions.viewVehicles !== true && (
                          <div style={{ marginLeft: '24px' }}>
                            <div style={{ fontSize: '0.9rem', color: '#6B7280', marginBottom: '8px' }}>
                              Select specific vehicle types:
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
                            {availableVehicles.map(vehicle => (
                                <label key={vehicle} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <input
                                  type="checkbox"
                                    checked={newRole.permissions.viewVehicles === true || newRole.permissions.viewVehicles.includes(vehicle)}
                                    onChange={(e) => {
                                      // If viewVehicles is currently true (all vehicles), handle intelligently
                                      if (newRole.permissions.viewVehicles === true && !e.target.checked) {
                                        // Convert from "all" to specific vehicles (all except this one)
                                        const allExceptThis = availableVehicles.filter(v => v !== vehicle);
                                        setNewRole(prev => ({
                                          ...prev,
                                          permissions: { ...prev.permissions, viewVehicles: allExceptThis }
                                        }));
                                      } else if (newRole.permissions.viewVehicles === true && e.target.checked) {
                                        // Already true, no change needed
                                        return;
                                      } else {
                                        // Normal array logic for specific vehicles
                                        const currentVehicles = Array.isArray(newRole.permissions.viewVehicles)
                                          ? newRole.permissions.viewVehicles
                                          : [];
                                        const updatedVehicles = e.target.checked
                                          ? [...currentVehicles, vehicle]
                                          : currentVehicles.filter(v => v !== vehicle);
                                        setNewRole(prev => ({
                                          ...prev,
                                          permissions: { ...prev.permissions, viewVehicles: updatedVehicles }
                                        }));
                                      }
                                    }}
                                />
                                <span style={{ textTransform: 'capitalize' }}>{vehicle}</span>
                            </label>
                          ))}
                        </div>
                          </div>
                        )}
                    </div>

                      {/* Geofence Access */}
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <input
                            type="checkbox"
                            checked={newRole.permissions.viewGeofences === true}
                            onChange={(e) => setNewRole(prev => ({
                              ...prev,
                              permissions: {
                                ...prev.permissions,
                                viewGeofences: e.target.checked ? true : []
                              }
                            }))}
                          />
                          <span>View All Geofences</span>
                        </label>

                        {newRole.permissions.viewGeofences !== true && (
                          <div style={{ marginLeft: '24px' }}>
                            <div style={{ fontSize: '0.9rem', color: '#6B7280', marginBottom: '8px' }}>
                              Select specific geofence types:
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
                            {availableGeofences.map(geofence => (
                                <label key={geofence} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <input
                                  type="checkbox"
                                    checked={newRole.permissions.viewGeofences === true || newRole.permissions.viewGeofences.includes(geofence)}
                                    onChange={(e) => {
                                      const currentGeofences = Array.isArray(newRole.permissions.viewGeofences)
                                        ? newRole.permissions.viewGeofences
                                        : [];
                                      const updatedGeofences = e.target.checked
                                        ? [...currentGeofences, geofence]
                                        : currentGeofences.filter(g => g !== geofence);
                                      setNewRole(prev => ({
                                        ...prev,
                                        permissions: { ...prev.permissions, viewGeofences: updatedGeofences }
                                      }));
                                    }}
                                />
                                <span style={{ textTransform: 'capitalize' }}>{geofence}</span>
                            </label>
                          ))}
                        </div>
                          </div>
                        )}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={createCustomRole}
                      disabled={isLoading || !newRole.name.trim()}
                      style={{
                        background: '#10B981',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        opacity: isLoading || !newRole.name.trim() ? 0.6 : 1
                      }}
                      onMouseOver={(e) => !e.target.disabled && (e.target.style.background = '#059669')}
                      onMouseOut={(e) => !e.target.disabled && (e.target.style.background = '#10B981')}
                    >
                      {isLoading ? 'Creating...' : 'Create Role'}
                    </button>

                    <button
                      onClick={() => setShowCreateRole(false)}
                      style={{
                        background: '#6B7280',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.background = '#4B5563'}
                      onMouseOut={(e) => e.target.style.background = '#6B7280'}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Edit Role Form */}
              {showEditRole && editingRole && (
                <div ref={editFormRef} style={{
                  marginBottom: '30px',
                  padding: '20px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  backgroundColor: '#F9FAFB'
                }}>
                  <h4 style={{ margin: '0 0 16px 0', color: '#374151' }}>Edit Role: {editingRole.name}</h4>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Role Name *
                    </label>
                    <input
                      type="text"
                      value={editRole.name}
                      onChange={(e) => setEditRole(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter role name"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Description
                    </label>
                    <textarea
                      value={editRole.description}
                      onChange={(e) => setEditRole(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Enter role description"
                      rows="3"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  {/* Permissions */}
                  <div style={{ marginBottom: '20px' }}>
                    <h5 style={{ margin: '0 0 12px 0', color: '#374151' }}>Permissions</h5>

                    {/* View Offices */}
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                          checked={editRole.permissions.viewOffices}
                          onChange={(e) => setEditRole(prev => ({
                            ...prev,
                            permissions: { ...prev.permissions, viewOffices: e.target.checked }
                          }))}
                        />
                        <span>View Offices</span>
                    </label>
                  </div>

                    {/* View Towers */}
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="checkbox"
                          checked={editRole.permissions.viewTowers}
                          onChange={(e) => setEditRole(prev => ({
                            ...prev,
                            permissions: { ...prev.permissions, viewTowers: e.target.checked }
                          }))}
                        />
                        <span>View Towers</span>
                    </label>
                  </div>

                    {/* Vehicle Access */}
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <input
                          type="checkbox"
                          checked={editRole.permissions.viewVehicles === true}
                          onChange={(e) => setEditRole(prev => ({
                            ...prev,
                            permissions: {
                              ...prev.permissions,
                              viewVehicles: e.target.checked ? true : []
                            }
                          }))}
                        />
                        <span>View All Vehicles</span>
                      </label>

                      {editRole.permissions.viewVehicles !== true && (
                        <div style={{ marginLeft: '24px' }}>
                          <div style={{ fontSize: '0.9rem', color: '#6B7280', marginBottom: '8px' }}>
                            Select specific vehicle types:
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
                          {availableVehicles.map(vehicle => (
                              <label key={vehicle} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <input
                                type="checkbox"
                                  checked={editRole.permissions.viewVehicles === true || editRole.permissions.viewVehicles.includes(vehicle)}
                                  onChange={(e) => {
                                    // If viewVehicles is currently true (all vehicles), handle intelligently
                                    if (editRole.permissions.viewVehicles === true && !e.target.checked) {
                                      // Convert from "all" to specific vehicles (all except this one)
                                      const allExceptThis = availableVehicles.filter(v => v !== vehicle);
                                      setEditRole(prev => ({
                                        ...prev,
                                        permissions: { ...prev.permissions, viewVehicles: allExceptThis }
                                      }));
                                    } else if (editRole.permissions.viewVehicles === true && e.target.checked) {
                                      // Already true, no change needed
                                      return;
                                    } else {
                                      // Normal array logic for specific vehicles
                                      const currentVehicles = Array.isArray(editRole.permissions.viewVehicles)
                                        ? editRole.permissions.viewVehicles
                                        : [];
                                      const updatedVehicles = e.target.checked
                                        ? [...currentVehicles, vehicle]
                                        : currentVehicles.filter(v => v !== vehicle);
                                      setEditRole(prev => ({
                                        ...prev,
                                        permissions: { ...prev.permissions, viewVehicles: updatedVehicles }
                                      }));
                                    }
                                  }}
                              />
                              <span style={{ textTransform: 'capitalize' }}>{vehicle}</span>
                          </label>
                        ))}
                      </div>
                        </div>
                      )}
                  </div>

                    {/* Geofence Access */}
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <input
                          type="checkbox"
                          checked={editRole.permissions.viewGeofences === true}
                          onChange={(e) => setEditRole(prev => ({
                            ...prev,
                            permissions: {
                              ...prev.permissions,
                              viewGeofences: e.target.checked ? true : []
                            }
                          }))}
                        />
                        <span>View All Geofences</span>
                      </label>

                      {editRole.permissions.viewGeofences !== true && (
                        <div style={{ marginLeft: '24px' }}>
                          <div style={{ fontSize: '0.9rem', color: '#6B7280', marginBottom: '8px' }}>
                            Select specific geofence types:
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px' }}>
                          {availableGeofences.map(geofence => (
                              <label key={geofence} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <input
                                type="checkbox"
                                  checked={editRole.permissions.viewGeofences === true || editRole.permissions.viewGeofences.includes(geofence)}
                                  onChange={(e) => {
                                    const currentGeofences = Array.isArray(editRole.permissions.viewGeofences)
                                      ? editRole.permissions.viewGeofences
                                      : [];
                                    const updatedGeofences = e.target.checked
                                      ? [...currentGeofences, geofence]
                                      : currentGeofences.filter(g => g !== geofence);
                                    setEditRole(prev => ({
                                      ...prev,
                                      permissions: { ...prev.permissions, viewGeofences: updatedGeofences }
                                    }));
                                  }}
                              />
                              <span style={{ textTransform: 'capitalize' }}>{geofence}</span>
                          </label>
                        ))}
                      </div>
                        </div>
                      )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={updateRolePermissions}
                    disabled={isLoading}
                    style={{
                      background: '#10B981',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      opacity: isLoading ? 0.6 : 1
                    }}
                    onMouseOver={(e) => !e.target.disabled && (e.target.style.background = '#059669')}
                    onMouseOut={(e) => !e.target.disabled && (e.target.style.background = '#10B981')}
                  >
                    {isLoading ? 'Updating...' : 'Update Role'}
                  </button>

                  <button
                    onClick={cancelEditRole}
                    style={{
                      background: '#6B7280',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.background = '#4B5563'}
                    onMouseOut={(e) => e.target.style.background = '#6B7280'}
                  >
                    Cancel
                  </button>
                </div>
                </div>
              )}

              {/* Existing Roles List */}
              <div>
                <h4 style={{ margin: '0 0 16px 0', color: '#374151' }}>Existing Roles</h4>
                <div style={{ display: 'grid', gap: '12px' }}>
                  {roles.map(role => (
                    <div key={role.id} style={{
                      padding: '16px',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      backgroundColor: '#FFFFFF'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div>
                          <h5 style={{ margin: '0 0 4px 0', color: '#374151', fontSize: '1.1rem' }}>
                            {role.name}
                            {role.isCustom && (
                              <span style={{
                                marginLeft: '8px',
                                fontSize: '0.8rem',
                                background: '#3B82F6',
                                color: 'white',
                                padding: '2px 6px',
                                borderRadius: '4px'
                              }}>
                                Custom
                              </span>
                            )}
                          </h5>
                          {role.description && (
                            <p style={{ margin: '0 0 8px 0', color: '#6B7280', fontSize: '0.9rem' }}>
                              {role.description}
                            </p>
                          )}
                        </div>

                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => startEditRole(role)}
                            style={{
                              background: '#3B82F6',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              fontSize: '0.8rem',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.background = '#2563EB'}
                            onMouseOut={(e) => e.target.style.background = '#3B82F6'}
                          >
                            Edit
                          </button>

                          {role.isCustom && (
                            <button
                              onClick={() => deleteCustomRole(role.id)}
                              style={{
                                background: '#EF4444',
                                color: 'white',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseOver={(e) => e.target.style.background = '#DC2626'}
                              onMouseOut={(e) => e.target.style.background = '#EF4444'}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>

                      <div style={{ fontSize: '0.85rem', color: '#6B7280' }}>
                        <div><strong>Offices:</strong> {role.permissions.viewOffices ? 'Yes' : 'No'}</div>
                        <div><strong>Towers:</strong> {role.permissions.viewTowers ? 'Yes' : 'No'}</div>
                        <div><strong>Vehicles:</strong> {
                          role.permissions.viewVehicles === true ? 'All' :
                          Array.isArray(role.permissions.viewVehicles) ? role.permissions.viewVehicles.join(', ') : 'None'
                        }</div>
                        <div><strong>Geofences:</strong> {
                          role.permissions.viewGeofences === true ? 'All' :
                          Array.isArray(role.permissions.viewGeofences) ? role.permissions.viewGeofences.join(', ') : 'None'
                        }</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              </div>
            )}

            {/* Temporary Access Tab */}
            {activeTab === 'tempAccess' && (
              <div className="temp-access-tab">
                <h3>Temporary Access Management</h3>
                <p style={{ color: '#6B7280', marginBottom: '20px' }}>
                  Generate time-limited access links for external users. Links expire automatically after the specified duration.
                </p>

                {/* Generate Temporary Access Form */}
                <div style={{
                  marginBottom: '30px',
                  padding: '20px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  backgroundColor: '#F9FAFB'
                }}>
                  <h4 style={{ margin: '0 0 16px 0', color: '#374151' }}>Generate Temporary Access Link</h4>

                  <div className="temp-access-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '16px', marginBottom: '16px', alignItems: 'end' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        Email Address *
                      </label>
                      <input
                        type="email"
                        value={tempAccessEmail}
                        onChange={(e) => setTempAccessEmail(e.target.value)}
                        placeholder="user@example.com"
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>

                    <div style={{ position: 'relative' }} data-duration-dropdown>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        Duration
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowDurationDropdown(!showDurationDropdown)}
                        style={{
                          padding: '10px 16px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          fontSize: '1rem',
                          backgroundColor: '#FFFFFF',
                          cursor: 'pointer',
                          minWidth: '120px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.borderColor = '#3B82F6';
                          e.target.style.backgroundColor = '#F8FAFC';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.borderColor = '#D1D5DB';
                          e.target.style.backgroundColor = '#FFFFFF';
                        }}
                      >
                        <span>{tempAccessDuration} {tempAccessDuration === 1 ? 'Hour' : 'Hours'}</span>
                        <span style={{ 
                          transform: showDurationDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease',
                          fontSize: '0.8rem'
                        }}>
                          ▼
                        </span>
                      </button>

                      {/* Dropdown Menu */}
                      {showDurationDropdown && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: '0',
                          right: '0',
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          zIndex: 1000,
                          marginTop: '4px',
                          overflow: 'scroll',
                          overflowX:"hidden",
                          height:"23vh"
                        }}>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((hours) => (
                            <button
                              key={hours}
                              type="button"
                              onClick={() => {
                                setTempAccessDuration(hours);
                                setShowDurationDropdown(false);
                              }}
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: 'none',
                                backgroundColor: tempAccessDuration === hours ? '#EBF8FF' : '#FFFFFF',
                                color: tempAccessDuration === hours ? '#1E40AF' : '#374151',
                                fontSize: '1rem',
                                textAlign: 'left',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                // borderBottom: hours < 9 ? '1px solid #F3F4F6' : 'none'
                                borderBottom:"1px solid #b8b8b86b" 
                              }}
                              onMouseEnter={(e) => {
                                if (tempAccessDuration !== hours) {
                                  e.target.style.backgroundColor = '#F8FAFC';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (tempAccessDuration !== hours) {
                                  e.target.style.backgroundColor = '#FFFFFF';
                                }
                              }}
                            >
                              {hours} {hours === 1 ? 'Hour' : 'Hours'}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div style={{ position: 'relative' }} data-role-dropdown>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                        Role
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                        style={{
                          padding: '10px 16px',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          fontSize: '1rem',
                          backgroundColor: '#FFFFFF',
                          cursor: 'pointer',
                          minWidth: '120px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '8px',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.borderColor = '#3B82F6';
                          e.target.style.backgroundColor = '#F8FAFC';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.borderColor = '#D1D5DB';
                          e.target.style.backgroundColor = '#FFFFFF';
                        }}
                      >
                        <span style={{ textTransform: 'capitalize' }}>{getRoleDisplayName(tempAccessRole)}</span>
                        <span style={{ 
                          transform: showRoleDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s ease',
                          fontSize: '0.8rem'
                        }}>
                          ▼
                        </span>
                      </button>

                      {/* Role Dropdown Menu */}
                      {showRoleDropdown && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: '0',
                          right: '0',
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #D1D5DB',
                          borderRadius: '6px',
                          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          zIndex: 1000,
                          marginTop: '4px',
                           overflow: 'scroll',
                          overflowX:"hidden",
                          height:"23vh"
                        }}>
                          {roles.map((role) => (
                            <button
                              key={role.id}
                              type="button"
                              onClick={() => {
                                setTempAccessRole(role.id); // Use role ID instead of name
                                setShowRoleDropdown(false);
                              }}
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                border: 'none',
                                backgroundColor: tempAccessRole === role.id ? '#EBF8FF' : '#FFFFFF',
                                color: tempAccessRole === role.id ? '#1E40AF' : '#374151',
                                fontSize: '0.8rem',
                                textAlign: 'left',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                // borderBottom: role !== roles[roles.length - 1] ? '1px solid #F3F4F6' : 'none',
                                borderBottom:"1px solid #b8b8b86b" 
                              }}
                              onMouseEnter={(e) => {
                                if (tempAccessRole !== role.id) {
                                  e.target.style.backgroundColor = '#F8FAFC';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (tempAccessRole !== role.id) {
                                  e.target.style.backgroundColor = '#FFFFFF';
                                }
                              }}
                            >
                              <div style={{ fontWeight: '500', textTransform: 'capitalize'}}>{role.name}</div>
                              <div style={{ fontSize: '0.8rem', color: '#6B7280', marginTop: '2px' }}>{role.description || 'No description'}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Auto Session Timeout */}
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
                    padding: '10px 14px', background: tempAccessAutoTimeout ? '#eff6ff' : '#f8fafc',
                    border: `1px solid ${tempAccessAutoTimeout ? '#93c5fd' : '#e2e8f0'}`,
                    borderRadius: '10px', transition: 'all 0.2s', marginBottom: '4px',
                  }}>
                    <input
                      type="checkbox"
                      checked={tempAccessAutoTimeout}
                      onChange={(e) => setTempAccessAutoTimeout(e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: '#004aad', cursor: 'pointer' }}
                    />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>
                        Auto Session Timeout
                      </div>
                      <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>
                        Re-verify OTP if user is inactive for 30 min and reloads the page
                      </div>
                    </div>
                  </label>

                  <div className="temp-access-buttons" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={generateTempAccessLink}
                      disabled={isGeneratingLink || !tempAccessEmail.trim()}
                      style={{
                        background: isGeneratingLink ? '#9CA3AF' : '#3B82F6',
                        color: 'white',
                        border: 'none',
                        padding: '12px 20px',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        fontWeight: '500',
                        cursor: isGeneratingLink ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        minHeight: '44px'
                      }}
                    >
                      {isGeneratingLink ? (
                          <span>Generating...</span>
                      ) : (
                          <span>Generate Link</span>
                      )}
                    </button>

                    {generatedLink && (
                      <button
                        onClick={copyTempAccessLink}
                        style={{
                          background: '#10B981',
                          color: 'white',
                          border: 'none',
                          padding: '12px 20px',
                          borderRadius: '6px',
                          fontSize: '1rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          minHeight: '44px'
                        }}
                      >
                        <span>Copy Link</span>
                      </button>
                    )}
          </div>

                  {generatedLink && (
                    <div style={{
                      marginTop: '16px',
                      padding: '12px',
                      backgroundColor: '#EFF6FF',
                      border: '1px solid #3B82F6',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      wordBreak: 'break-all'
                    }}>
                      <strong>Generated Link:</strong><br />
                      <code style={{ color: '#1E40AF' }}>{generatedLink}</code>
                    </div>
                  )}
                </div>


                {/* Active Temporary Access Links */}
                <div>
                  <div className="temp-access-links-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' }}>
                    <h4 style={{ margin: '0', color: '#374151', flex: '1', minWidth: '200px' }}>Active Temporary Access Links</h4>
                    <button
                      onClick={cleanupInvalidEntries}
                      disabled={isLoading}
                      style={{
                        background: isLoading ? '#9CA3AF' : '#F59E0B',
                        color: 'white',
                        border: 'none',
                        padding: '10px 16px',
                        borderRadius: '6px',
                        fontSize: '0.9rem',
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.2s',
                        minHeight: '44px',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        if (!isLoading) {
                          e.target.style.background = '#D97706';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isLoading) {
                          e.target.style.background = '#F59E0B';
                        }
                      }}
                    >
                      {isLoading ? 'Cleaning...' : 'Clean Up Invalid Entries'}
                    </button>
                  </div>
                  
                  {tempAccessLinks.length === 0 ? (
                    <div style={{
                      padding: '20px',
                      textAlign: 'center',
                      color: '#6B7280',
                      backgroundColor: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px'
                    }}>
                      No active temporary access links
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {tempAccessLinks.map((link) => (
                        <div key={link.id} style={{
                          padding: '16px',
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px',
                          backgroundColor: 'white'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                            <div>
                              <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                                {link.email}
                              </div>
                              <div style={{ fontSize: '0.9rem', color: '#6B7280' }}>
                                Expires: {(() => {
                                  try {
                                    const date = new Date(link.expiresAt);
                                    return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
                                  } catch (error) {
                                    return 'Invalid Date';
                                  }
                                })()}
                              </div>
                            </div>
                            <button
                              onClick={() => revokeTempAccess(link.id)}
                              style={{
                                background: '#EF4444',
                                color: 'white',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                fontSize: '0.8rem',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s'
                              }}
                            >
                              Revoke
                            </button>
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#6B7280', wordBreak: 'break-all' }}>
                            <strong>Link:</strong> {link.accessLink}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            </>
          </div>



          {/* Status Messages for Permissions Tab */}
          {activeTab === 'permissions' && (error || success) && (
            <div className={`alert-container ${(error || success) ? 'has-alert' : ''}`}>
              {error && (
                <div className="alert alert-error">
                  {error}
                  <button onClick={clearMessages} className="alert-close">&times;</button>
                </div>
              )}
              {success && (
                <div className="alert alert-success">
                  {success}
                  <button onClick={clearMessages} className="alert-close">&times;</button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* User Removal Confirmation Dialog */}
        {showRemoveConfirm && userToRemove && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
            }}>
              <h3 style={{ margin: '0 0 16px 0', color: '#DC2626' }}>
                ⚠️ Confirm User Removal
              </h3>

              <div style={{ marginBottom: '20px' }}>
                <p style={{ margin: '0 0 12px 0', color: '#374151' }}>
                  Are you sure you want to remove this user from the system?
                </p>

                <div style={{
                  padding: '12px',
                  backgroundColor: '#FEF2F2',
                  border: '1px solid #FECACA',
                  borderRadius: '6px',
                  marginBottom: '16px'
                }}>
                  <div style={{ fontWeight: '600', color: '#DC2626', marginBottom: '4px' }}>
                    User Details:
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#374151' }}>
                    <div><strong>Name:</strong> {userToRemove.displayName || 'N/A'}</div>
                    <div><strong>Email:</strong> {userToRemove.email}</div>
                    <div><strong>UID:</strong> {userToRemove.uid}</div>
                  </div>
                </div>

                <div style={{
                  padding: '12px',
                  backgroundColor: '#FEF3C7',
                  border: '1px solid #FDE68A',
                  borderRadius: '6px',
                  marginBottom: '16px'
                }}>
                  <div style={{ fontWeight: '600', color: '#D97706', marginBottom: '4px' }}>
                    ⚠️ Warning:
                  </div>
                  <div style={{ fontSize: '0.9rem', color: '#92400E' }}>
                    This action will permanently delete the user from Firestore and cannot be undone.
                    The user will lose access to the system immediately.
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={cancelUserRemoval}
                  style={{
                    background: '#6B7280',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#4B5563'}
                  onMouseOut={(e) => e.target.style.background = '#6B7280'}
                >
                  Cancel
                </button>

                <button
                  onClick={deleteUserFromFirestore}
                  disabled={isLoading}
                  style={{
                    background: '#EF4444',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    opacity: isLoading ? 0.6 : 1
                  }}
                  onMouseOver={(e) => !e.target.disabled && (e.target.style.background = '#DC2626')}
                  onMouseOut={(e) => !e.target.disabled && (e.target.style.background = '#EF4444')}
                >
                  {isLoading ? 'Removing...' : 'Yes, Remove User'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminCommandPanel = (props) =>
  createPortal(<AdminCommandPanelInner {...props} />, document.body);

export default AdminCommandPanel;